import { Button, Card, Input } from "@result/rui/components";
import { useForm } from "@result/rui/hooks";
import { useTranslation } from "react-i18next";

import SelectLookupTableItem from "#/components/SelectLookupTableItem";
import * as LTI from "#/def/lookup-tables.ident.gen";
import useLoginInfo from "#/hooks/useLoginInfo";

import schema from "./schema";

export default function ProfileForm({ user, breeder, ...props }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const form = useForm({
    schema: schema(),
    onSubmit: props.onSubmit,
    initialValues: {
      // we will not actually allow editing user data and we
      // do not even process it on submit, but we want to show it in the form,
      // so we will put it in the initial values
      user,

      // slice of breeder data that we want to show in the form,
      // we will also not allow editing most of it,
      // but we want to show it in the form,
      // so we will put it in the initial values
      breeder: {
        telefon: "",
        naslovZaPosiljanje: {
          ulica: "",
          hisnaStevilka: "",
          naslovDrugaVrstica: "",
          postnaStevilka: "",
          posta: "",
          drzava: "",
          ...breeder?.naslovZaPosiljanje,
        },
        ...breeder,
      },
    },
  });

  const isLoading = form.isLoading || props.isLoading;
  const isDisabled = isLoading;

  return (
    <form onSubmit={form.submit}>
      <UserDetails form={form} />

      {breeder && <BreederDetails form={form} breeder={breeder} />}

      <div className="is-flex is-justify-content-flex-end is-gap-1">
        <Button
          color="light"
          label={t("form.back")}
          leftIcon="icon-chevron-left"
          onClick={() => navigate("/")}
        />

        <Button
          isSubmit={true}
          isDisabled={isDisabled || !breeder}
          isLoading={isLoading}
          color="black"
          leftIcon="icon-circle-check"
          label={t("form.save")}
        />
      </div>
    </form>
  );
}

function UserDetails({ form }) {
  const { t } = useTranslation();

  const { isOtpConfigured, setupOTP } = useLoginInfo();

  return (
    <Card
      title={t("profile.user-details")}
      rightHeaderElement={
        <Button
          label={t("profile.otp")}
          icon="icon-shield-user"
          iconColor="white"
          iconSize="medium"
          outlined={true}
          isDisabled={isOtpConfigured}
          color="warning"
          tooltip={
            isOtpConfigured
              ? t("profile.otp.setupAlreadyDone")
              : t("profile.otp.setup")
          }
          tooltipAlign="right"
          tooltipColor="warning"
          onClick={setupOTP}
        />
      }>
      <div className="is-flex is-flex-direction-column">
        <Input
          type="text"
          formField={form.getField("user.imeTmp")}
          isDisabled={true}
          label={t("profile.form.field.name")}
        />
        <Input
          type="text"
          formField={form.getField("user.priimekTmp")}
          isDisabled={true}
          label={t("profile.form.field.surname")}
        />
        <Input
          type="text"
          formField={form.getField("user.username")}
          isDisabled={true}
          label={t("profile.form.field.username")}
        />
        <Input
          type="text"
          formField={form.getField("user.email")}
          isDisabled={true}
          label={t("profile.form.field.email")}
        />
      </div>
    </Card>
  );
}

function BreederDetails({ form, breeder }) {
  const { t } = useTranslation();

  return (
    <Card title={t("profile.breeder-details")} subTitle={breeder.naziv}>
      <div className="is-flex is-flex-direction-column">
        <Input
          type="text"
          formField={form.getField("breeder.telefon")}
          label={t("profile.form.field.phone")}
        />

        <h1 className="is-size-5 has-text-weight-bold mt-4 mb-2">
          {t("profile.form.sending-address")}
        </h1>
        <Input
          type="text"
          formField={form.getField("breeder.naslovZaPosiljanje.ulica")}
          label={t("profile.form.field.street")}
        />
        <Input
          type="text"
          formField={form.getField("breeder.naslovZaPosiljanje.hisnaStevilka")}
          label={t("profile.form.field.street-no")}
        />
        <Input
          type="text"
          formField={form.getField(
            "breeder.naslovZaPosiljanje.naslovDrugaVrstica"
          )}
          label={t("profile.form.field.address-line-2")}
        />
        <Input
          type="text"
          formField={form.getField("breeder.naslovZaPosiljanje.postnaStevilka")}
          label={t("profile.form.field.postal-code")}
        />
        <Input
          type="text"
          formField={form.getField("breeder.naslovZaPosiljanje.posta")}
          label={t("profile.form.field.city")}
        />
        <SelectLookupTableItem
          formField={form.getField("breeder.naslovZaPosiljanje.drzava")}
          label={t("profile.form.field.country")}
          ident={LTI.SifDrzava}
        />
      </div>
    </Card>
  );
}
